/** The evidence matrix (assessment §2.2; screens §F7): one row per indicator that has evidence, each cell its average on the −1 to +1 scale, a bar, the item count and its fact chips. */
import type { EvidenceMatrix } from "@bsim/contracts";
import { formatMetric } from "@gxs/metric-defs";

export const COMPETENCY_NAMES = { C1: "Financial acumen", C2: "Strategic trade-off judgment", C3: "Risk and regulatory thinking", C4: "Leadership and governance" } as const;

/** Each indicator's name and what it reads, in one sentence. */
const INDICATORS: Readonly<Record<string, readonly [string, string]>> = {
  "1.1": ["Call-the-year skill", "How much closer the team's forecast of the year was than \"same as last year\"."],
  "1.2": ["Expected direction", "Whether the direction the team expected for each change matched what happened."],
  "1.3": ["Mechanism", "Whether the team's reasons explain how each change works."],
  "2.1": ["Strategy consistency", "How often the team's changes follow the priorities it set in round 0, or explain why they depart."],
  "2.2": ["Short-termism gap", "How much of the team's value this year rests on reported numbers rather than the next two years."],
  "2.3": ["Trade-off articulation", "Whether the team's reasons name what each change gives up."],
  "2.4": ["Strategic cards", "How the team answered the strategic event cards it received."],
  "3.1": ["Appetite quality", "How many of the team's own risk limits are informative for its bank."],
  "3.2": ["Appetite adherence", "Whether the plan stayed inside the team's own limits, and fixed any breach."],
  "3.3": ["Risk-card response", "How the team answered the risk event cards it received."],
  "3.4": ["Risk sensing", "Whether the team answered the risk officer's red flags with a change or a reason."],
  "4.1": ["Dissent resolution", "How objections between the risk officer and the CEO were settled."],
  "4.2": ["Decision discipline", "Whether plans were signed by the team rather than carried forward or rescued."],
  "4.3": ["Participation breadth", "How many people on the team wrote its reasons."],
};

export const SCALE_WORDS = "Scale: \u22121 is the weakest reading, 0 neutral, +1 the strongest.";

// A reading on the -1 to +1 scale, not points: only the shared metric printer's sign and two places are borrowed (formatPoints would add a unit).
const signed = (v: number): string => formatMetric(v, "index", { precision: 2, signed: true });

/**
 * What a cell's reading says in words, for the indicators whose items are yes/no (the mean on the -1 to +1 scale gives the share that were yes): "matched 1 of 1", "named what it gives up: 1 of 2".
 * Other indicators keep the count of items; the number beside it is their reading.
 */
const YES_NO: Readonly<Record<string, (yes: number, of: number) => string>> = {
  "1.2": (y, n) => `matched ${y} of ${n}`,
  "1.3": (y, n) => `explained how it works: ${y} of ${n}`,
  "2.3": (y, n) => `named what it gives up: ${y} of ${n}`,
};
export function cellReading(indicatorId: string, meanScore: number | null, count: number): string {
  const words = YES_NO[indicatorId];
  // No average on the scale (nothing to score yet, or an item type that has none): say so, never a bare count the facilitator cannot read.
  if (meanScore === null) return "no reading yet";
  if (words === undefined) return `${count} ${count === 1 ? "item" : "items"}`;
  return words(Math.round(((Math.max(-1, Math.min(1, meanScore)) + 1) / 2) * count), count);
}

/** \u22121\u20261 as a bar growing from the middle: cool to the right, warm to the left. */
function ScoreBar({ score }: { readonly score: number }) {
  return <span className="gx-fac-mbar" aria-hidden="true"><i data-sign={score >= 0 ? "pos" : "neg"} style={{ width: `${Math.abs(score) * 50}%` }} /></span>;
}

export function EvidenceMatrixView({ matrix }: { readonly matrix: EvidenceMatrix }) {
  const rows = matrix.rows.filter((row) => row.cells.some((c) => c.evidenceCount > 0));
  if (rows.length === 0) return <p className="gx-card-b gx-muted" data-testid="evidence.matrix">No evidence has been recorded for this team yet.</p>;
  return (
    <div className="gx-fac-matrix" data-testid="evidence.matrix">
      {rows.some((r) => r.cells.some((c) => c.meanScore !== null)) ? <p className="gx-small gx-muted gx-fac-scale">{SCALE_WORDS}</p> : null}
      <table className="gx-table">
        <thead><tr><th>Indicator</th>{matrix.rounds.map((r) => <th key={r}>Round {r}</th>)}</tr></thead>
        <tbody>
          {rows.map((row) => {
            const [name, what] = INDICATORS[row.indicatorId] ?? [COMPETENCY_NAMES[row.competency], ""];
            return (
              <tr key={row.indicatorId}>
                <th scope="row">{name}{what === "" ? null : <span className="gx-small gx-muted gx-fac-what">{what}</span>}</th>
                {matrix.rounds.map((r) => {
                  const cell = row.cells.find((c) => c.round === r);
                  return (
                    <td key={r} className="gx-fac-mcell">
                      {cell === undefined || cell.evidenceCount === 0 ? <span className="gx-faint">–</span> : (
                        <>
                          {cell.meanScore === null ? null : <span className="gx-fac-mread"><><span className="gx-num"><b>{signed(cell.meanScore)}</b></span><ScoreBar score={cell.meanScore} /></></span>}
                          <span className="gx-small gx-faint">{cellReading(row.indicatorId, cell.meanScore, cell.evidenceCount)}</span>
                          {cell.factIds.map((f) => <span key={f} className="gx-fac-fact gx-num" title={f}>{f.split(".").at(-1)}</span>)}
                        </>
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
